import { useNavigate, useParams } from "@tanstack/react-router";
import { Button, message, Skeleton, Tag, Tooltip } from "antd";
import { PageHead } from "@/components/PageHead.tsx";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { PageScrollable } from "@/layout/PageScrollable.tsx";
import { RaidOverView } from "@/raids/raidDetail/RaidOverView.tsx";
import { RaidResourcePanel } from "@/raids/raidDetail/RaidResourcePanel.tsx";
import { RaidParticipant } from "@/raids/raidDetail/RaidParticipant.tsx";
import { useGetRaid } from "@/raids/rest/RaidQueries.ts";
import type { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PinEntryModal } from "@/raids/PinEntryModal.tsx";

/** Nur-Lese-Ansicht mit Polling; Bearbeiten öffnet PIN-Modal → Route `/raids/$raidId/edit`. */
export function RaidDetails() {
  const { raidId: raidIdParam } = useParams({ from: "/raids_/$raidId/" });
  const raidId = useMemo(() => Number(raidIdParam), [raidIdParam]);
  const isValidRaidId = Number.isFinite(raidId) && raidId > 0;

  const navigate = useNavigate();
  const [msgApi, contextHolder] = message.useMessage();
  const [pinModalOpen, setPinModalOpen] = useState(false);

  const navigateBack = useCallback(() => {
    void navigate({ to: "/raids" });
  }, [navigate]);

  useEffect(() => {
    if (!isValidRaidId) {
      void msgApi.error("Ungültige Raid-ID");
      navigateBack();
    }
  }, [isValidRaidId, msgApi, navigateBack]);

  const {
    data: raid,
    isLoading,
    isError,
    error,
  } = useGetRaid(raidId, isValidRaidId, true);

  const displayRaid = raid ?? null;

  useEffect(() => {
    if (isError) {
      void msgApi.error(
        "Laden des Raids fehlgeschlagen. " + (error as AxiosError)?.message,
      );
      navigateBack();
    }
  }, [isError, error, msgApi, navigateBack]);

  if (!isValidRaidId) return null;

  const closedTag = (
    <>
      {!isLoading && displayRaid?.status === "CLOSED" && (
        <div>
          <Tooltip title={"Raid beendet"}>
            <Tag color="volcano" className={"m-w-28! m-h-"}>
              Beendet
            </Tag>
          </Tooltip>
        </div>
      )}
    </>
  );

  const cmdBar = (
    <div className={"flex justify-end items-center gap-2"}>
      {isLoading && (
        <Skeleton.Button active shape="round" className={"m-w-28!"} />
      )}
      {!isLoading && displayRaid && (
        <Button type="primary" onClick={() => setPinModalOpen(true)}>
          Bearbeiten
        </Button>
      )}
      {closedTag}
    </div>
  );

  return (
    <PageScrollable
      title={
        <PageHead
          title="Raid Details"
          rightComponent={cmdBar}
          leftComponent={
            <Tooltip title={"Zurück"}>
              <Button icon={<ArrowLeftOutlined />} onClick={navigateBack} />
            </Tooltip>
          }
        />
      }
    >
      <div
        className={
          "flex w-full max-w-full flex-col gap-2 max-md:h-auto max-md:flex-none md:min-h-0 md:flex-1"
        }
      >
        {contextHolder}
        <PinEntryModal
          raidId={raidId}
          open={pinModalOpen}
          onClose={() => setPinModalOpen(false)}
        />
        <div className={"flex w-full shrink-0 flex-col gap-2"}>
          <RaidOverView
            raid={displayRaid}
            isLoading={isLoading}
            isSaving={false}
            isEditAble={false}
          />
        </div>
        <div
          className={
            "flex w-full min-w-0 max-w-full flex-col gap-2 duration-300 max-md:h-auto max-md:flex-none md:min-h-0 md:flex-1 md:flex-row"
          }
        >
          <RaidResourcePanel
            raid={displayRaid}
            isLoading={isLoading}
            isSaving={false}
            isEditAble={false}
            callBackUpdateResources={() => {}}
            onFocusInput={() => {}}
          />
          <RaidParticipant
            raid={displayRaid}
            isLoading={isLoading}
            isSaving={false}
            isEditAble={false}
            callBackAddParticipant={() => {}}
            callBackRemoveParticipant={() => {}}
            onFocusInput={() => {}}
          />
        </div>
      </div>
    </PageScrollable>
  );
}
